import { trackUserBaseSelector } from '@fullstory/analytics';
import { trackElementArchived } from '@fullstory/analytics/elements';
import { FsEvent } from '@fullstory/fs-script-api';
import { intl } from '@fullstory/intl';
import { ConfirmModal } from '@fullstory/junk-drawer-ui';
import { NativePlatform } from '@fullstory/proto/fs/api/segment';
import { ValidateNamedElementResponse } from '@fullstory/proto/fs/services/dx-objects';
import {
  Classification,
  NamedElement,
  Suggestion,
} from '@fullstory/proto/fs/services/search-settings';
import { Flag } from '@fullstory/proto/fs/shared/flags';
import { useFlags } from '@fullstory/react-hooks';
import {
  AppDispatch,
  IssueSilenceUpdate,
  NamedElementsRequestActions,
  fullstorySelectors,
  SpecObjectInfo,
  allSpecElementsBySlug,
} from '@fullstory/redux';
import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createApiElementSelector } from '../components';
import {
  CaptureOptionsUpdate,
  NamedElementModalSource,
  NamedElementModalVariant,
} from '../components/NamedElementModal/types';
import { NamedElementEditableDetails } from '../types';
import { useSelectedFsSpecObjectSlugs } from '@fullstory/events-ui';

interface SharedNamedElementsProps {
  details: NamedElementEditableDetails | null;
  onSave: (
    namedElement: NamedElement,
    captureOptions: CaptureOptionsUpdate,
    silence: IssueSilenceUpdate | null,
    fsSpecSlugs: string[] | null,
    hasExistingIssue: boolean,
    classification: Classification | null,
  ) => void;
  onClose: (saved?: boolean) => void;
  onArchive: (id: string) => void;
  onUnarchive(id: string): void;
  onRejectSuggestion?: (suggestedElementId: string, feedback: string) => void;
  initialSpecSlugs?: string[];
}

export const useSharedNamedElementsActions = ({
  details,
  onSave,
  onClose,
  onArchive,
  onUnarchive,
  onRejectSuggestion,
  initialSpecSlugs,
}: SharedNamedElementsProps) => {
  const flags = useFlags();
  const orgId = useSelector(fullstorySelectors.getOrgId);
  const webOnly = !flags.hasBit(Flag.FeatNativeMobile);
  const dispatch = useDispatch<AppDispatch>();

  const [validation, setValidation] = useState<ValidateNamedElementResponse | null>(null);
  const userAnalytics = useSelector(trackUserBaseSelector);

  const specElementsBySlug = useSelector(allSpecElementsBySlug);
  const isExistingMapping = useCallback(
    (specObject: SpecObjectInfo): boolean =>
      !!specObject.mapping?.elementId &&
      specObject.mapping.elementId === details?.namedElement?.namedElement?.id,
    [details?.namedElement?.namedElement?.id],
  );
  const {
    selectedFsSpecObjectSlugs,
    setSelectedFsSpecObjectSlugs,
    fsSpecEnabled,
    fsSpecRoleOptions,
    areRolesDirty,
    existingSpecObjects,
  } = useSelectedFsSpecObjectSlugs(specElementsBySlug, initialSpecSlugs || null, isExistingMapping);

  const [confirmArchive, confirmModal] = useConfirm(
    intl.formatMessage({ defaultMessage: 'Archive Element?', id: 'gFAAeG' }),
    intl.formatMessage({
      defaultMessage:
        'This Element will no longer be watched, or blocked from frustration signals.',
      id: 'lP0AOh',
    }),
  );

  // Like window.confirm but with our own ConfirmModal
  function useConfirm(modalTitle: string, message?: string) {
    const [open, setOpen] = useState(false);
    // The extra arrow function is needed because without it,
    // React thinks we're using the callback version of useState,
    // and we end up with a void state value. What we actually want is
    // a function state value, so we have to use a function that returns
    // the function we want.
    const [onModalConfirm, setModalConfirm] = useState(() => () => {});
    const [onModalCancel, setModalCancel] = useState(() => () => {});
    const getUserConfirmation = () =>
      new Promise<boolean>((resolve, _) => {
        setOpen(true);
        const doResolve = (ok: boolean) => () => () => {
          resolve(ok);
          setOpen(false);
        };
        setModalConfirm(doResolve(true));
        setModalCancel(doResolve(false));
      });

    const modal = open && (
      <ConfirmModal
        title={modalTitle}
        message={message}
        confirmAction={onModalConfirm}
        onHide={onModalCancel}
        testId="named-element-modal-confirm"
      />
    );
    return [getUserConfirmation, modal] as const;
  }

  const platformSelectors = useMemo(() => {
    if (details?.namedElement?.namedElement?.platformSelectors.length === 0) {
      // Create a skeleton platform selector
      return [{ selector: '', platforms: [] }];
    }

    if (webOnly) {
      // If the organization only has web enabled, make sure all selectors are web.
      return details?.namedElement?.namedElement?.platformSelectors.map(ps => ({
        ...ps,
        platforms: [NativePlatform.NATIVE_PLATFORM_WEB],
      }));
    }

    return details?.namedElement?.namedElement?.platformSelectors;
  }, [details?.namedElement?.namedElement?.platformSelectors, webOnly]);

  const handleOnSaveNamedElement = useCallback(
    async (
      initialDetails: NamedElementEditableDetails | null,
      details: NamedElementEditableDetails | null,
      variant: NamedElementModalVariant,
      source: NamedElementModalSource,
      isSuggestion: boolean = false,
      classification: Classification | null,
    ) => {
      const hasExistingIssue = (initialDetails?.issuesData?.issues?.length || 0) > 0;
      FsEvent.recordCustomEventV2('Named Element Saved', {
        is_new: variant === NamedElementModalVariant.new,
        source: NamedElementModalSource[source],
      });

      if (isSuggestion) {
        const name_length_diff =
          (details?.namedElement?.namedElement?.name.length || 1) /
          (initialDetails?.namedElement?.namedElement?.name.length || 1);
        const description_length_diff =
          (details?.namedElement?.namedElement?.description.length || 1) /
          (initialDetails?.namedElement?.namedElement?.description.length || 1);
        const selector_length_diff =
          (details?.namedElement?.namedElement?.platformSelectors[0]?.selector.length || 1) /
          (initialDetails?.namedElement?.namedElement?.platformSelectors[0]?.selector.length || 1);
        const absolute_name_length_diff = Math.abs(1 - name_length_diff);
        const absolute_description_length_diff = Math.abs(1 - description_length_diff);
        const absolute_selector_length_diff = Math.abs(1 - selector_length_diff);
        const selector_trim_method =
          details?.namedElement?.namedElement?.suggestion?.reqSelectorTrim ?? '';

        FsEvent.recordCustomEventV2('Suggestion Saved', {
          name_length_diff,
          description_length_diff,
          selector_length_diff,
          absolute_name_length_diff,
          absolute_description_length_diff,
          absolute_selector_length_diff,
          selector_trim_method,
        });

        // Set suggestion status to approved when saving a suggestion
        if (details?.namedElement?.namedElement?.suggestion) {
          details.namedElement.namedElement.suggestion.status = Suggestion.Status.STATUS_APPROVED;
        }
      }

      const v = await dispatch(
        NamedElementsRequestActions.validateElement(orgId, details?.namedElement?.namedElement!),
      );

      const apiName = details?.namedElement?.namedElement?.apiName ?? '';
      const apiSelector = apiName ? createApiElementSelector(apiName).selector : '';

      const valid =
        v != null &&
        v.name?.valid != undefined &&
        v?.platformSelectors.every(ps => ps.valid) &&
        platformSelectors &&
        platformSelectors.every(
          ps => ps.platforms.length > 0 || (apiSelector && ps.selector === apiSelector),
        );

      if (!valid) {
        setValidation(v);
        return;
      }

      onSave(
        details?.namedElement?.namedElement!,
        {
          prev: {
            watched: initialDetails?.watched || false,
            rageClicksBlocked: initialDetails?.ignoreRageClicks || false,
            deadClicksBlocked: initialDetails?.ignoreDeadClicks || false,
          },
          next: {
            watched: details?.watched || false,
            rageClicksBlocked: details?.ignoreRageClicks || false,
            deadClicksBlocked: details?.ignoreDeadClicks || false,
          },
        },
        {
          prev: initialDetails?.silence,
          next: details?.silence,
        },
        selectedFsSpecObjectSlugs?.map(s => s.value) || null,
        hasExistingIssue,
        classification,
      );

      onClose(true);
    },
    [dispatch, onClose, onSave, orgId, platformSelectors, selectedFsSpecObjectSlugs],
  );

  const handleArchiveNamedElement = useCallback(
    async (archived: boolean) => {
      const customEventName = 'Named Element Archived';
      // Keeping this for backwards compatibility with the old analytics event.
      FsEvent.recordCustomEventV2(customEventName, {
        archived,
      });
      trackElementArchived({
        ...userAnalytics,
        element_id_str: details?.namedElement?.namedElement?.id || '',
        is_archived_bool: archived,
      });

      if (archived) {
        if (details?.watched || details?.ignoreRageClicks || details?.ignoreDeadClicks) {
          const ok = await confirmArchive();
          if (!ok) return;
        }
        onArchive(details?.namedElement?.namedElement?.id || '');
      } else {
        const v = await dispatch(
          NamedElementsRequestActions.validateElement(orgId, details?.namedElement?.namedElement!),
        );
        const valid =
          v != null &&
          v.name?.valid != undefined &&
          v?.platformSelectors.every(ps => ps.valid) &&
          platformSelectors &&
          platformSelectors.every(ps => ps.platforms.length > 0);

        if (!valid) {
          setValidation(v);
          return;
        }
        onUnarchive(details?.namedElement?.namedElement?.id || '');
      }

      onClose();
    },
    [
      confirmArchive,
      details?.ignoreDeadClicks,
      details?.ignoreRageClicks,
      details?.namedElement?.namedElement,
      details?.watched,
      dispatch,
      onArchive,
      onClose,
      onUnarchive,
      orgId,
      platformSelectors,
      userAnalytics,
    ],
  );

  const rejectSuggestion = useCallback(
    async (suggestedElementId: string, feedback: string) => {
      const selector_trim_method =
        details?.namedElement?.namedElement?.suggestion?.reqSelectorTrim ?? '';

      FsEvent.recordCustomEventV2('Suggestion Rejected', {
        feedback,
        suggestedElementId,
        name: details?.namedElement?.namedElement?.name ?? '',
        description: details?.namedElement?.namedElement?.description ?? '',
        selector: details?.namedElement?.namedElement?.platformSelectors[0]?.selector ?? '',
        selector_trim_method,
      });

      await onRejectSuggestion?.(suggestedElementId, feedback);
      onClose();
    },
    [onClose, onRejectSuggestion, details?.namedElement?.namedElement],
  );

  return {
    handleOnSaveNamedElement,
    handleArchiveNamedElement,
    validation,
    setSelectedFsSpecObjectSlugs,
    selectedFsSpecObjectSlugs,
    fsSpecEnabled,
    fsSpecRoleOptions,
    areRolesDirty,
    existingSpecObjects,
    confirmModal,
    rejectSuggestion,
  };
};
